import React, { useState, useMemo } from 'react';
import { formatNumber } from '../../../../services/stockApi';

/**
 * 호가창 컴포넌트
 * stock-insight의 OrderBook.tsx를 React로 포팅
 * 매수벽/매도벽 감지 기능 포함
 */
const OrderBook = ({ data, currentPrice = 0, onRefresh }) => {
  const [selectedPrice, setSelectedPrice] = useState(null);

  // 매수벽/매도벽 감지 (평균 대비 3배 이상)
  const { sellWall, buyWall, maxVolume } = useMemo(() => {
    if (!data || !data.asks || !data.bids) {
      return { sellWall: null, buyWall: null, maxVolume: 1 };
    }

    const avgAskVol = data.asks.reduce((sum, ask) => sum + ask.volume, 0) / data.asks.length;
    const avgBidVol = data.bids.reduce((sum, bid) => sum + bid.volume, 0) / data.bids.length;

    const sellWall = data.asks.find((ask) => ask.volume >= avgAskVol * 3);
    const buyWall = data.bids.find((bid) => bid.volume >= avgBidVol * 3);

    const maxVol = Math.max(
      ...data.asks.map((ask) => ask.volume),
      ...data.bids.map((bid) => bid.volume)
    );

    return { sellWall, buyWall, maxVolume: maxVol };
  }, [data]);

  // 총 잔량
  const totalAskVolume = data?.asks?.reduce((sum, ask) => sum + ask.volume, 0) || 0;
  const totalBidVolume = data?.bids?.reduce((sum, bid) => sum + bid.volume, 0) || 0;
  const totalVolume = totalAskVolume + totalBidVolume;

  // 매수/매도 비율
  const bidRatio = totalVolume > 0 ? (totalBidVolume / totalVolume) * 100 : 50;
  const askRatio = totalVolume > 0 ? (totalAskVolume / totalVolume) * 100 : 50;

  // 가격 포맷
  const formatPrice = (price) => {
    if (!price) return '-';
    return price.toLocaleString() + '원';
  };

  // 등락률 계산
  const getChangeRate = (price) => {
    if (!currentPrice || !price) return null;
    return ((price - currentPrice) / currentPrice * 100).toFixed(2);
  };

  if (!data || !data.asks || !data.bids) {
    return (
      <div className="py-10 px-5 text-center bg-gray-50 border border-gray-200 rounded-2xl text-gray-400">
        <p>호가 데이터가 없습니다</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold text-gray-900 m-0">호가창</h3>
        {onRefresh && (
          <button
            className="py-1.5 px-3 text-xs bg-gray-50 border border-gray-200 rounded-lg cursor-pointer transition-colors hover:bg-gray-100"
            onClick={onRefresh}
          >
            새로고침
          </button>
        )}
      </div>

      {/* 매수벽/매도벽 알림 */}
      {(sellWall || buyWall) && (
        <div className="flex items-center gap-2 py-2.5 px-3 bg-amber-400/10 border border-amber-400/30 rounded-lg mb-3">
          <span className="text-sm">🚧</span>
          <span className="text-[13px] text-gray-900">
            {sellWall && <span className="text-blue-500 font-medium">매도벽 {formatPrice(sellWall.price)}</span>}
            {sellWall && buyWall && ' / '}
            {buyWall && <span className="text-red-500 font-medium">매수벽 {formatPrice(buyWall.price)}</span>}
          </span>
        </div>
      )}

      {/* 호가 테이블 */}
      <div className="mb-3">
        {/* 매도 호가 (위쪽, 높은 가격부터) */}
        <div>
          {[...data.asks].sort((a, b) => b.price - a.price).map((ask, index) => {
            const isSellWall = sellWall?.price === ask.price;
            const percentage = (ask.volume / maxVolume) * 100;
            const changeRate = getChangeRate(ask.price);

            return (
              <div
                key={`ask-${index}`}
                className={`relative flex justify-between items-center py-2 px-3 rounded-md cursor-pointer transition-colors mb-0.5 hover:bg-gray-50 ${selectedPrice === ask.price ? 'outline outline-2 outline-blue-500 -outline-offset-2' : ''} ${isSellWall ? 'outline outline-2 outline-amber-400 -outline-offset-2' : ''}`}
                onClick={() => setSelectedPrice(ask.price)}
              >
                <div
                  className="absolute top-0 right-0 bottom-0 rounded-md transition-[width] duration-300 bg-blue-500/15"
                  style={{ width: `${percentage}%` }}
                />
                <div className="relative z-[1] flex items-center gap-2">
                  <span className="text-sm font-medium font-mono text-blue-500">{ask.price.toLocaleString()}</span>
                  {changeRate && (
                    <span className="text-[11px] font-medium text-red-500">+{changeRate}%</span>
                  )}
                  {isSellWall && <span className="text-xs">🚧</span>}
                </div>
                <div className="relative z-[1] text-[13px] font-mono text-gray-500">
                  {formatNumber(ask.volume)}
                </div>
              </div>
            );
          })}
        </div>

        {/* 현재가 구분선 */}
        {currentPrice > 0 && (
          <div className="flex justify-between items-center py-2.5 px-3 my-2 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <span className="text-xs text-gray-400">현재가</span>
            <span className="text-[15px] font-semibold text-gray-900">{currentPrice.toLocaleString()}원</span>
          </div>
        )}

        {/* 매수 호가 (아래쪽, 높은 가격부터) */}
        <div>
          {[...data.bids].sort((a, b) => b.price - a.price).map((bid, index) => {
            const isBuyWall = buyWall?.price === bid.price;
            const percentage = (bid.volume / maxVolume) * 100;
            const changeRate = getChangeRate(bid.price);

            return (
              <div
                key={`bid-${index}`}
                className={`relative flex justify-between items-center py-2 px-3 rounded-md cursor-pointer transition-colors mb-0.5 hover:bg-gray-50 ${selectedPrice === bid.price ? 'outline outline-2 outline-blue-500 -outline-offset-2' : ''} ${isBuyWall ? 'outline outline-2 outline-amber-400 -outline-offset-2' : ''}`}
                onClick={() => setSelectedPrice(bid.price)}
              >
                <div
                  className="absolute top-0 right-0 bottom-0 rounded-md transition-[width] duration-300 bg-red-500/15"
                  style={{ width: `${percentage}%` }}
                />
                <div className="relative z-[1] flex items-center gap-2">
                  <span className="text-sm font-medium font-mono text-red-500">{bid.price.toLocaleString()}</span>
                  {changeRate && (
                    <span className="text-[11px] font-medium text-blue-500">{changeRate}%</span>
                  )}
                  {isBuyWall && <span className="text-xs">🚧</span>}
                </div>
                <div className="relative z-[1] text-[13px] font-mono text-gray-500">
                  {formatNumber(bid.volume)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 총 잔량 요약 */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-3 rounded-xl text-center bg-blue-500/10">
          <span className="block text-xs text-gray-400 mb-1">총 매도</span>
          <span className="text-base font-semibold font-mono text-blue-500">{formatNumber(totalAskVolume)}</span>
        </div>
        <div className="p-3 rounded-xl text-center bg-red-500/10">
          <span className="block text-xs text-gray-400 mb-1">총 매수</span>
          <span className="text-base font-semibold font-mono text-red-500">{formatNumber(totalBidVolume)}</span>
        </div>
      </div>

      {/* 매수/매도 비율 바 */}
      <div className="mb-3">
        <div className="flex justify-between mb-1.5 text-xs text-gray-500">
          <span>매수 {bidRatio.toFixed(1)}%</span>
          <span>매도 {askRatio.toFixed(1)}%</span>
        </div>
        <div className="flex h-2 rounded overflow-hidden bg-gray-50">
          <div className="bg-red-500 transition-[width] duration-300" style={{ width: `${bidRatio}%` }} />
          <div className="bg-blue-500 transition-[width] duration-300" style={{ width: `${askRatio}%` }} />
        </div>
      </div>

      {/* 도움말 */}
      <div className="py-2.5 px-3 bg-gray-50 rounded-lg text-xs text-gray-400 md:hidden">
        <p className="m-0">🚧 <strong className="text-gray-500">벽</strong>: 평균 잔량의 3배 이상 (지지/저항선 역할)</p>
      </div>
      <div className="hidden md:block py-2.5 px-3 bg-gray-50 rounded-lg text-xs text-gray-400">
        <p className="m-0">🚧 <strong className="text-gray-500">벽</strong>: 평균 잔량의 3배 이상 (지지/저항선 역할)</p>
      </div>
    </div>
  );
};

export default OrderBook;
